import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { DisplayValueDescriptor } from '@vendasta/uikit';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { getImageSrc } from '../../../core/image-utilities';
import { convertSecondsToTimeString } from '../../../core/metrics.helper';
import { AnalyticsService } from '../../analytics/analytics.service';
import { DateRangeSelectorService } from '../../date-range-selector';
import { ChartSeries, fillInCurrentAverage, fillInCurrentTotal } from '../../metrics';
import { AVERAGE_SECONDS_ON_WEBSITE_CONFIG, TOTAL_VISITORS_CONFIG } from './ga-overview-metrics.config';
import { GaOverviewMetricsService } from './ga-overview-metrics.service';

@Component({
  selector: 'app-ga-overview-metrics',
  template: `
    @if ((loading$ | async) === false) {
      @if (isGaEnabled) {
        <div class="metric-card-holder">
          <app-metrics-card
            class="half-width-card"
            [header]="cardConfig.totalVisitors.header"
            [currentPeriod]="totalVisitorsCurrent$ | async"
            [previousPeriod]="totalVisitorsPrevious$ | async"
            [hideTopStats]="gaViewNotFound$ | async"
            [currentAgeText]="ageText"
            >
            <ng-template [ngTemplateOutlet]="edgeCases"></ng-template>
            <div chart-legend>
              <mat-card-actions>
                <mat-divider></mat-divider>
                <button class="footer" mat-button (click)="viewMore('total-visitors')">
                  {{ 'ANALYTICS.ANALYTICS_PANEL.BUTTONS.VIEW_MORE' | translate }}
                </button>
              </mat-card-actions>
            </div>
          </app-metrics-card>
          <app-metrics-card
            class="half-width-card"
            [header]="cardConfig.averageSecondsOnWebsite.header"
            [currentPeriod]="averageSecondsCurrent$ | async"
            [formatValueDisplayFunc]="convertSecondsToHHMMSS"
            [hideTopStats]="gaViewNotFound$ | async"
            [previousPeriod]="averageSecondsPrevious$ | async"
            [currentAgeText]="ageText"
            >
            <ng-template [ngTemplateOutlet]="edgeCases"></ng-template>
            <div chart-legend>
              <mat-card-actions>
                <mat-divider></mat-divider>
                <button class="footer" mat-button (click)="viewMore('average-seconds-on-website')">
                  {{ 'ANALYTICS.ANALYTICS_PANEL.BUTTONS.VIEW_MORE' | translate }}
                </button>
              </mat-card-actions>
            </div>
          </app-metrics-card>
        </div>
      }
    } @else {
      @if (!isGaEnabled) {
        <div class="metric-card-holder">
          <div class="loading-state">
            <app-loading-state-card
              class="loading-state"
              [header]="cardConfig.totalVisitors.header"
            ></app-loading-state-card>
          </div>
          <div class="loading-state">
            <app-loading-state-card [header]="cardConfig.averageSecondsOnWebsite.header"></app-loading-state-card>
          </div>
        </div>
      }
    }
    
    <ng-template #edgeCases>
      @if (error$ | async) {
        <div class="api-error-container">{{ 'ANALYTICS.FAILED_GET_DATA' | translate }}</div>
      } @else {
        @if (gaViewNotFound$ | async) {
          <div class="connect-ga">
            <img src="{{ addChartImage }}" alt="" />
            <br />
            {{ 'ANALYTICS.ANALYTICS_PANEL.GATHER_SITE_INSIGHT' | translate }}
            <br />
            @if ((connectGALinkLoading$ | async) === false) {
              @if ((connectGALinkError$ | async) === false) {
                <a href="{{ connectGALink$ | async }}" class="connect-google-analytics-button" target="_blank">
                  {{ 'ANALYTICS.ANALYTICS_PANEL.CONNECT_GA_VIEW_CTA' | translate }}
                </a>
              }
            }
          </div>
        }
      }
    </ng-template>
    
    `,
  styleUrls: ['./ga-overview-metrics.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class GaOverviewMetricsComponent implements OnInit {
  @Input() siteId: string;

  readonly cardConfig = {
    totalVisitors: TOTAL_VISITORS_CONFIG,
    averageSecondsOnWebsite: AVERAGE_SECONDS_ON_WEBSITE_CONFIG,
  };
  ageText: string;
  addChartImage = getImageSrc('images/add-chart.svg');

  loading$: Observable<boolean>;
  error$: Observable<boolean>;
  isGaEnabled = false;

  totalVisitorsCurrent$: Observable<DisplayValueDescriptor>;
  totalVisitorsPrevious$: Observable<DisplayValueDescriptor>;
  averageSecondsCurrent$: Observable<DisplayValueDescriptor>;
  averageSecondsPrevious$: Observable<DisplayValueDescriptor>;

  gaViewNotFound$: Observable<boolean>;
  gaViewError$: Observable<boolean>;
  connectGALinkLoading$: Observable<boolean>;
  connectGALinkError$: Observable<boolean>;
  connectGALink$: Observable<string>;

  constructor(
    private gaOverviewMetricsService: GaOverviewMetricsService,
    private snowplow: ProductAnalyticsService,
    private analyticsService: AnalyticsService,
    private dateRangeSelectorService: DateRangeSelectorService,
    private translateService: TranslateService,
    private router: Router,
  ) {
    this.ageText = this.translateService.instant('ANALYTICS.DATE_RANGE_SELECTOR.TIME_PERIOD.LAST_7_DAYS');
    this.connectGALink$ = this.analyticsService.connectGALink$;
    this.connectGALinkError$ = this.analyticsService.connectGALinkError$;
    this.connectGALinkLoading$ = this.analyticsService.connectGALinkLoading$;
    this.gaViewNotFound$ = this.analyticsService.gaViewNotFound$;

    this.loading$ = combineLatest([
      this.gaOverviewMetricsService.loadingGARecords$,
      this.analyticsService.gaViewLoading$,
    ]).pipe(map(([recordsLoading, viewLoading]) => recordsLoading || viewLoading));
    this.error$ = combineLatest([
      this.gaOverviewMetricsService.loadingGARecordsError$,
      this.analyticsService.gaViewError$,
    ]).pipe(map(([recordsErr, viewErr]) => recordsErr || viewErr));
    this.totalVisitorsCurrent$ = fillInCurrentTotal(
      this.gaOverviewMetricsService.gaRecords$.pipe(
        map(([current]) => {
          return [
            {
              name: 'Total Visitors',
              series: current.totalVisitors,
            } as ChartSeries,
          ];
        }),
      ),
    );
    this.totalVisitorsPrevious$ = fillInCurrentTotal(
      this.gaOverviewMetricsService.gaRecords$.pipe(
        map(([, previous]) => {
          return [
            {
              name: 'Total Visitors',
              series: previous.totalVisitors,
            } as ChartSeries,
          ];
        }),
      ),
    );
    this.averageSecondsCurrent$ = fillInCurrentAverage(
      this.gaOverviewMetricsService.gaRecords$.pipe(
        map(([current]) => {
          return [
            {
              name: 'Average Seconds',
              series: current.averageSecondsOnWebsite,
            } as ChartSeries,
          ];
        }),
      ),
    );
    this.averageSecondsPrevious$ = fillInCurrentAverage(
      this.gaOverviewMetricsService.gaRecords$.pipe(
        map(([, previous]) => {
          return [
            {
              name: 'Average Seconds',
              series: previous.averageSecondsOnWebsite,
            } as ChartSeries,
          ];
        }),
      ),
    );
  }

  ngOnInit(): void {
    this.analyticsService.isGaEnabled.subscribe((data) => {
      this.isGaEnabled = data;
    });
    this.analyticsService.getGaIsEnabledState(this.siteId).subscribe();
  }

  /**
   * Takes users to navigate to analysis tab
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public viewMore(graphName: string): void {
    this.dateRangeSelectorService.setTimeRange('7days');
    const url = `/site/${this.siteId}/analytics`;
    this.router.navigateByUrl(url);
  }

  convertSecondsToHHMMSS(sec: DisplayValueDescriptor): DisplayValueDescriptor {
    sec.value = convertSecondsToTimeString(sec.value as number);
    sec.displayType = 'string';
    return sec;
  }
}
